import { useRef, type ComponentProps, type PointerEvent } from 'react';

type BagPointer = {
  pointerId: number;
  clientX: number;
  clientY: number;
  pointerType: string;
};

/** Horizontal touch gestures browse the bag; an upward gesture takes an item out. */
export function BagItemButton({
  onDragStart,
  ...props
}: Omit<ComponentProps<'button'>, 'onDragStart'> & {
  onDragStart: (pointer: BagPointer) => void;
}) {
  const press = useRef<BagPointer | null>(null);

  const pointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0 || !e.isPrimary) return;
    const pointer = {
      pointerId: e.pointerId,
      clientX: e.clientX,
      clientY: e.clientY,
      pointerType: e.pointerType,
    };
    if (e.pointerType === 'mouse') {
      e.preventDefault();
      onDragStart(pointer);
    } else {
      // Keep native pan-x available until the player clearly pulls upward.
      press.current = pointer;
    }
  };

  return (
    <button
      {...props}
      onPointerDown={pointerDown}
      onPointerMove={(e) => {
        const start = press.current;
        if (!start || start.pointerId !== e.pointerId) return;
        const dx = Math.abs(e.clientX - start.clientX);
        const dy = e.clientY - start.clientY;
        if (Math.max(dx, Math.abs(dy)) < 10) return;
        // Lock the first clear direction, so a scrolling finger cannot pick up an item later.
        press.current = null;
        if (dy >= -dx) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        onDragStart({
          pointerId: e.pointerId,
          clientX: e.clientX,
          clientY: e.clientY,
          pointerType: e.pointerType,
        });
      }}
      onPointerUp={() => {
        press.current = null;
      }}
      onPointerCancel={() => {
        press.current = null;
      }}
      onLostPointerCapture={() => {
        press.current = null;
      }}
    />
  );
}
